/**
 * Адаптеры границы workspace-контракта: перевод старых числовых ID и epoch ms
 * в контрактные значения и проверка ответов новых API во время выполнения.
 *
 * Проверка строгая: отсутствующее поле — ошибка, а не подставленный null,
 * иначе расхождение backend и frontend выглядело бы как «данных нет».
 */

import { LocalizedError } from '@/utils/displayMessage';
import {
	ACCESS_DECISIONS,
	OBSERVATION_STATES,
	OCCURRENCE_LOCATION_KINDS,
	RESOURCE_KINDS,
	WORKFLOW_STATUSES,
	RISK_COMPLETENESS,
	SENSITIVE_GROUPS,
	SENSITIVITY_LEVELS,
	SUMMARY_COMPLETENESS,
	SERIES_OBSERVATIONS,
	type DataTypeOccurrence,
	type DataTypeUsage,
	type EndpointSeries,
	type EventAssignee,
	type EventWorkflow,
	type Page,
	type ResourceAccess,
	type ResourceCard,
	type ResourceFacets,
	type ResourceKind,
	type ResourceRef,
	type RiskSummary,
	type SensitiveGroups,
	type WorkflowChange,
	type WorkspaceSummary,
} from './workspaceTypes';

/**
 * Ответ API не соответствует контракту. Пользователь видит переводимое
 * сообщение с полем, разработчик — ещё и detail.
 */
export class WorkspaceContractError extends LocalizedError {
	readonly path: string;
	readonly detail: string;

	constructor(path: string, detail: string) {
		super({ messageKey: 'common:errors.unexpectedResponse', params: { path } });
		this.name = 'WorkspaceContractError';
		this.path = path;
		this.detail = detail;
	}
}

/**
 * Ссылка на ресурс по ID старого API. Число допускается только безопасное
 * целое: за пределами Number.MAX_SAFE_INTEGER точность уже потеряна.
 */
export function legacyResourceRef(kind: ResourceKind, id: number | string): ResourceRef {
	if (typeof id === 'number') {
		if (!Number.isSafeInteger(id)) {
			throw new RangeError('Resource id is not a safe integer');
		}
		return { kind, id: String(id) };
	}
	if (id.trim() === '') {
		throw new RangeError('Resource id must not be empty');
	}
	return { kind, id };
}

/** Строковый ключ ссылки для Map/Set и сравнения */
export function resourceRefKey(ref: ResourceRef): string {
	return `${ref.kind}:${ref.id}`;
}

/**
 * Время старого API (epoch ms) в ISO UTC. 0 и отсутствие значения в старых
 * ответах означают «неизвестно» (например, S3 last_checked), а не 1970 год.
 */
export function epochMillisToIso(value: number | null | undefined): string | null {
	if (value === null || value === undefined || value === 0) {
		return null;
	}
	if (!Number.isFinite(value) || value < 0) {
		throw new RangeError('Epoch milliseconds must be a finite non-negative number');
	}
	return new Date(value).toISOString();
}

/* ────────────────────────────────────────────
 * Проверка ответов новых API
 * ──────────────────────────────────────────── */

type Json = Record<string, unknown>;

function object(value: unknown, path: string): Json {
	if (value === null || typeof value !== 'object' || Array.isArray(value)) {
		throw new WorkspaceContractError(path, 'expected an object');
	}
	return value as Json;
}

function field(source: Json, key: string, path: string): unknown {
	if (!(key in source)) {
		throw new WorkspaceContractError(`${path}.${key}`, 'field is missing');
	}
	return source[key];
}

function string(value: unknown, path: string): string {
	if (typeof value !== 'string') {
		throw new WorkspaceContractError(path, 'expected a string');
	}
	return value;
}

function number(value: unknown, path: string): number {
	if (typeof value !== 'number' || !Number.isFinite(value)) {
		throw new WorkspaceContractError(path, 'expected a finite number');
	}
	return value;
}

function nullable<T>(value: unknown, path: string, read: (v: unknown, p: string) => T): T | null {
	return value === null ? null : read(value, path);
}

function oneOf<T extends string>(allowed: readonly T[]) {
	return (value: unknown, path: string): T => {
		const text = string(value, path);
		if (!(allowed as readonly string[]).includes(text)) {
			throw new WorkspaceContractError(path, 'unexpected enum value');
		}
		return text as T;
	};
}

function list<T>(value: unknown, path: string, read: (v: unknown, p: string) => T): T[] {
	if (!Array.isArray(value)) {
		throw new WorkspaceContractError(path, 'expected an array');
	}
	return value.map((item, index) => read(item, `${path}[${index}]`));
}

const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

function isoTime(value: unknown, path: string): string {
	const text = string(value, path);
	if (!ISO_UTC.test(text)) {
		throw new WorkspaceContractError(path, 'expected UTC ISO time');
	}
	return text;
}

const readKind = oneOf(RESOURCE_KINDS);
const readObservation = oneOf(OBSERVATION_STATES);
const readSensitivity = oneOf(SENSITIVITY_LEVELS);
const readCompleteness = oneOf(RISK_COMPLETENESS);

function readRef(value: unknown, path: string): ResourceRef {
	const source = object(value, path);
	const id = string(field(source, 'id', path), `${path}.id`);
	if (id.trim() === '') {
		throw new WorkspaceContractError(`${path}.id`, 'must not be empty');
	}
	return { kind: readKind(field(source, 'kind', path), `${path}.kind`), id };
}

function readRisk(value: unknown, path: string): RiskSummary {
	const source = object(value, path);
	return {
		model_id: string(field(source, 'model_id', path), `${path}.model_id`),
		score: nullable(field(source, 'score', path), `${path}.score`, number),
		completeness: readCompleteness(field(source, 'completeness', path), `${path}.completeness`),
		computed_at: nullable(field(source, 'computed_at', path), `${path}.computed_at`, isoTime),
		factors: list(field(source, 'factors', path), `${path}.factors`, (item, itemPath) => {
			const factor = object(item, itemPath);
			return {
				code: string(field(factor, 'code', itemPath), `${itemPath}.code`),
				contribution: number(field(factor, 'contribution', itemPath), `${itemPath}.contribution`),
				evidence_refs: list(
					field(factor, 'evidence_refs', itemPath),
					`${itemPath}.evidence_refs`,
					string,
				),
			};
		}),
	};
}

/** Карточка ресурса из ответа API; бросает WorkspaceContractError при расхождении */
export function parseResourceCard(value: unknown, path = 'card'): ResourceCard {
	const source = object(value, path);
	const at = (key: string) => field(source, key, path);
	const placement = object(at('placement'), `${path}.placement`);
	const placementAt = (key: string) => field(placement, key, `${path}.placement`);
	return {
		ref: readRef(at('ref'), `${path}.ref`),
		name: string(at('name'), `${path}.name`),
		owner: nullable(at('owner'), `${path}.owner`, string),
		placement: {
			region_id: nullable(placementAt('region_id'), `${path}.placement.region_id`, string),
			cluster_id: nullable(placementAt('cluster_id'), `${path}.placement.cluster_id`, string),
			namespace: nullable(placementAt('namespace'), `${path}.placement.namespace`, string),
		},
		observation: readObservation(at('observation'), `${path}.observation`),
		last_observed_at: nullable(at('last_observed_at'), `${path}.last_observed_at`, isoTime),
		last_successful_scan_at: nullable(
			at('last_successful_scan_at'),
			`${path}.last_successful_scan_at`,
			isoTime,
		),
		risk: nullable(at('risk'), `${path}.risk`, readRisk),
		engine: nullable(at('engine'), `${path}.engine`, string),
		instance: nullable(at('instance'), `${path}.instance`, (item, itemPath) => {
			const instance = object(item, itemPath);
			return {
				id: string(field(instance, 'id', itemPath), `${itemPath}.id`),
				name: string(field(instance, 'name', itemPath), `${itemPath}.name`),
			};
		}),
		sensitivity: nullable(at('sensitivity'), `${path}.sensitivity`, readSensitivity),
		data_type_ids: nullable(at('data_type_ids'), `${path}.data_type_ids`, (item, itemPath) =>
			list(item, itemPath, string),
		),
		object_count: nullable(at('object_count'), `${path}.object_count`, number),
		sensitive_field_count: nullable(
			at('sensitive_field_count'),
			`${path}.sensitive_field_count`,
			number,
		),
		size_bytes: nullable(at('size_bytes'), `${path}.size_bytes`, number),
		endpoint_count: nullable(at('endpoint_count'), `${path}.endpoint_count`, number),
	};
}

/** Страница из ответа API с проверкой каждого элемента */
export function parsePage<T>(
	value: unknown,
	parseItem: (item: unknown, path: string) => T,
	path = 'page',
): Page<T> {
	const source = object(value, path);
	const total = number(field(source, 'total', path), `${path}.total`);
	const filtered = number(field(source, 'filtered_total', path), `${path}.filtered_total`);
	if (filtered > total) {
		throw new WorkspaceContractError(`${path}.filtered_total`, 'exceeds total');
	}
	return {
		items: list(field(source, 'items', path), `${path}.items`, parseItem),
		total,
		filtered_total: filtered,
		limit: number(field(source, 'limit', path), `${path}.limit`),
		offset: number(field(source, 'offset', path), `${path}.offset`),
	};
}

const readLocationKind = oneOf(OCCURRENCE_LOCATION_KINDS);

/** Размещение типа данных (E3.6a) */
export function parseDataTypeOccurrence(value: unknown, path = 'occurrence'): DataTypeOccurrence {
	const source = object(value, path);
	const at = (key: string) => field(source, key, path);
	return {
		id: string(at('id'), `${path}.id`),
		resource: readRef(at('resource'), `${path}.resource`),
		resource_name: nullable(at('resource_name'), `${path}.resource_name`, string),
		location_kind: readLocationKind(at('location_kind'), `${path}.location_kind`),
		location_id: string(at('location_id'), `${path}.location_id`),
		location_name: nullable(at('location_name'), `${path}.location_name`, string),
		path: nullable(at('path'), `${path}.path`, string),
		direction: nullable(at('direction'), `${path}.direction`, string),
		last_observed_at: nullable(at('last_observed_at'), `${path}.last_observed_at`, isoTime),
		evidence_refs: list(at('evidence_refs'), `${path}.evidence_refs`, string),
	};
}

/** Использование типов данных: ответ { items } с числами и временем наблюдения каждого типа */
export function parseDataTypeUsage(value: unknown, path = 'usage'): DataTypeUsage[] {
	const source = object(value, path);
	return list(field(source, 'items', path), `${path}.items`, (item, itemPath) => {
		const usage = object(item, itemPath);
		const at = (key: string) => field(usage, key, itemPath);
		return {
			data_type_id: string(at('data_type_id'), `${itemPath}.data_type_id`),
			storages: number(at('storages'), `${itemPath}.storages`),
			services: number(at('services'), `${itemPath}.services`),
			places: number(at('places'), `${itemPath}.places`),
			first_observed_at: nullable(
				at('first_observed_at'),
				`${itemPath}.first_observed_at`,
				isoTime,
			),
			last_observed_at: nullable(at('last_observed_at'), `${itemPath}.last_observed_at`, isoTime),
		};
	});
}

/** Страница карточек ресурсов */
export function parseResourceCardPage(value: unknown): Page<ResourceCard> {
	return parsePage(value, parseResourceCard);
}

/** Счётчики каталога; каждый вид обязан иметь число — отсутствие не становится нулём */
export function parseResourceFacets(value: unknown, path = 'facets'): ResourceFacets {
	const source = object(value, path);
	const byKind = object(field(source, 'by_kind', path), `${path}.by_kind`);
	const counts = Object.fromEntries(
		RESOURCE_KINDS.map((kind) => [
			kind,
			number(field(byKind, kind, `${path}.by_kind`), `${path}.by_kind.${kind}`),
		]),
	) as Record<ResourceKind, number>;
	return {
		by_kind: counts,
		filtered_total: number(field(source, 'filtered_total', path), `${path}.filtered_total`),
		with_sensitive_data: number(
			field(source, 'with_sensitive_data', path),
			`${path}.with_sensitive_data`,
		),
		high_risk: number(field(source, 'high_risk', path), `${path}.high_risk`),
		engines_by_kind: enginesByKind(source, path),
	};
}

/* Движки по видам; сервер до этой сборки их не отдаёт — тогда списки пусты (подписи без движков) */
function enginesByKind(source: Json, path: string): Record<ResourceKind, string[]> {
	const value = source.engines_by_kind;
	const engines = value === undefined ? {} : object(value, `${path}.engines_by_kind`);
	return Object.fromEntries(
		RESOURCE_KINDS.map((kind) => [
			kind,
			engines[kind] === undefined
				? []
				: list(engines[kind], `${path}.engines_by_kind.${kind}`, string),
		]),
	) as Record<ResourceKind, string[]>;
}

const readWorkflowStatus = oneOf(WORKFLOW_STATUSES);

/** Разбор события из ответа API; бросает WorkspaceContractError при расхождении */
export function parseEventWorkflow(value: unknown, path = 'workflow'): EventWorkflow {
	const source = object(value, path);
	const at = (key: string) => field(source, key, path);
	return {
		event_id: string(at('event_id'), `${path}.event_id`),
		status: readWorkflowStatus(at('status'), `${path}.status`),
		assignee_id: nullable(at('assignee_id'), `${path}.assignee_id`, string),
		assignee_name: nullable(at('assignee_name'), `${path}.assignee_name`, string),
		version: number(at('version'), `${path}.version`),
		updated_at: nullable(at('updated_at'), `${path}.updated_at`, isoTime),
	};
}

/** Запись истории разбора */
export function parseWorkflowChange(value: unknown, path = 'change'): WorkflowChange {
	const source = object(value, path);
	const at = (key: string) => field(source, key, path);
	return {
		id: string(at('id'), `${path}.id`),
		actor_id: string(at('actor_id'), `${path}.actor_id`),
		actor_name: nullable(at('actor_name'), `${path}.actor_name`, string),
		changed_at: isoTime(at('changed_at'), `${path}.changed_at`),
		before: parseEventWorkflow(at('before'), `${path}.before`),
		after: parseEventWorkflow(at('after'), `${path}.after`),
	};
}

/** Кандидат в ответственные */
export function parseEventAssignee(value: unknown, path = 'assignee'): EventAssignee {
	const source = object(value, path);
	return {
		id: string(field(source, 'id', path), `${path}.id`),
		display_name: string(field(source, 'display_name', path), `${path}.display_name`),
	};
}

/** Ссылки события на ресурсы (E2.4) */
export function parseResourceRefs(value: unknown, path = 'resource_refs'): ResourceRef[] {
	return list(value, path, readRef);
}

/** Список карточек вне страницы (явный состав группы); бросает WorkspaceContractError */
export function parseResourceCards(value: unknown, path = 'items'): ResourceCard[] {
	return list(value, path, parseResourceCard);
}

/** Сводка обзора; бросает WorkspaceContractError при расхождении */
const readSummaryCompleteness = oneOf(SUMMARY_COMPLETENESS);
const readObservationOfHour = oneOf(SERIES_OBSERVATIONS);

/** Почасовой ряд эндпоинта; бросает WorkspaceContractError при расхождении */
export function parseEndpointSeries(value: unknown, path = 'series'): EndpointSeries {
	const source = object(value, path);
	const at = (key: string) => field(source, key, path);
	if (at('unit') !== 'requests' || at('aggregation') !== 'estimated') {
		throw new WorkspaceContractError(`${path}.unit`, 'unexpected unit or aggregation');
	}
	return {
		unit: 'requests',
		aggregation: 'estimated',
		coverage_from: nullable(at('coverage_from'), `${path}.coverage_from`, isoTime),
		as_of: isoTime(at('as_of'), `${path}.as_of`),
		points: list(at('points'), `${path}.points`, (item, itemPath) => {
			const point = object(item, itemPath);
			const p = (key: string) => field(point, key, itemPath);
			return {
				from: isoTime(p('from'), `${itemPath}.from`),
				to: isoTime(p('to'), `${itemPath}.to`),
				value: nullable(p('value'), `${itemPath}.value`, number),
				observation: readObservationOfHour(p('observation'), `${itemPath}.observation`),
			};
		}),
	};
}

export function parseWorkspaceSummary(value: unknown, path = 'summary'): WorkspaceSummary {
	const source = object(value, path);
	const at = (key: string) => field(source, key, path);
	return {
		sources_total: number(at('sources_total'), `${path}.sources_total`),
		open_events: number(at('open_events'), `${path}.open_events`),
		critical_events: number(at('critical_events'), `${path}.critical_events`),
		high_events: number(at('high_events'), `${path}.high_events`),
		sensitive_fields: nullable(at('sensitive_fields'), `${path}.sensitive_fields`, number),
		sensitive_sources: nullable(
			at('sensitive_sources'),
			`${path}.sensitive_sources`,
			(item, itemPath) => {
				const sources = object(item, itemPath);
				const count = (key: string) => number(field(sources, key, itemPath), `${itemPath}.${key}`);
				return { storages: count('storages'), services: count('services') };
			},
		),
		sensitive_fields_by_category: nullable(
			at('sensitive_fields_by_category'),
			`${path}.sensitive_fields_by_category`,
			(item, itemPath) => {
				const categories = object(item, itemPath);
				const count = (key: string) =>
					number(field(categories, key, itemPath), `${itemPath}.${key}`);
				return { pii: count('pii'), pci: count('pci'), secrets: count('secrets') };
			},
		),
		sensitive_fields_by_group: nullable(
			at('sensitive_fields_by_group'),
			`${path}.sensitive_fields_by_group`,
			(item, itemPath) => {
				const groups = object(item, itemPath);
				return Object.fromEntries(
					SENSITIVE_GROUPS.map((group) => [
						group,
						number(field(groups, group, itemPath), `${itemPath}.${group}`),
					]),
				) as SensitiveGroups;
			},
		),
		coverage: (() => {
			const coverage = object(at('coverage'), `${path}.coverage`);
			return {
				sensitive_fields: readSummaryCompleteness(
					field(coverage, 'sensitive_fields', `${path}.coverage`),
					`${path}.coverage.sensitive_fields`,
				),
				history_from: nullable(
					field(coverage, 'history_from', `${path}.coverage`),
					`${path}.coverage.history_from`,
					isoTime,
				),
			};
		})(),
		as_of: isoTime(at('as_of'), `${path}.as_of`),
		series: list(at('series'), `${path}.series`, (item, itemPath) => {
			const bucket = object(item, itemPath);
			const bucketAt = (key: string) => field(bucket, key, itemPath);
			return {
				bucket_start: isoTime(bucketAt('bucket_start'), `${itemPath}.bucket_start`),
				bucket_end: isoTime(bucketAt('bucket_end'), `${itemPath}.bucket_end`),
				open_events: nullable(bucketAt('open_events'), `${itemPath}.open_events`, number),
				critical_events: nullable(
					bucketAt('critical_events'),
					`${itemPath}.critical_events`,
					number,
				),
				sources_total: nullable(bucketAt('sources_total'), `${itemPath}.sources_total`, number),
				sensitive_fields: nullable(
					bucketAt('sensitive_fields'),
					`${itemPath}.sensitive_fields`,
					number,
				),
			};
		}),
	};
}

const readDecision = oneOf(ACCESS_DECISIONS);

/** Права ресурса из снимков доступа (E3.4c) */
export function parseResourceAccess(value: unknown, path = 'access'): ResourceAccess {
	const source = object(value, path);
	const at = (key: string) => field(source, key, path);
	return {
		resource: readRef(at('resource'), `${path}.resource`),
		observation: readObservation(at('observation'), `${path}.observation`),
		last_successful_scan_at: nullable(
			at('last_successful_scan_at'),
			`${path}.last_successful_scan_at`,
			isoTime,
		),
		last_attempt: nullable(at('last_attempt'), `${path}.last_attempt`, (item, itemPath) => {
			const attempt = object(item, itemPath);
			return {
				result: string(field(attempt, 'result', itemPath), `${itemPath}.result`),
				at: nullable(field(attempt, 'at', itemPath), `${itemPath}.at`, isoTime),
				message: nullable(field(attempt, 'message', itemPath), `${itemPath}.message`, string),
			};
		}),
		coverage: list(at('coverage'), `${path}.coverage`, (item, itemPath) => {
			const coverage = object(item, itemPath);
			return {
				surface: string(field(coverage, 'surface', itemPath), `${itemPath}.surface`),
				state: string(field(coverage, 'state', itemPath), `${itemPath}.state`),
				reason: nullable(field(coverage, 'reason', itemPath), `${itemPath}.reason`, string),
			};
		}),
		grants: parsePage(
			at('grants'),
			(item, itemPath) => {
				const grant = object(item, itemPath);
				const g = (key: string) => field(grant, key, itemPath);
				return {
					id: string(g('id'), `${itemPath}.id`),
					principal: string(g('principal'), `${itemPath}.principal`),
					actions: list(g('actions'), `${itemPath}.actions`, string),
					target: string(g('target'), `${itemPath}.target`),
					decision: readDecision(g('decision'), `${itemPath}.decision`),
					conditions: list(g('conditions'), `${itemPath}.conditions`, string),
					inherited_from: list(g('inherited_from'), `${itemPath}.inherited_from`, string),
					evidence_refs: list(g('evidence_refs'), `${itemPath}.evidence_refs`, string),
				};
			},
			`${path}.grants`,
		),
	};
}
